// @flow
import * as React from 'react';
import { Text } from 'react-native';
import { Input, ThemeContext } from 'react-native-elements';
import * as Yup from 'yup';
import { get } from 'lodash';
import {
    defineMessages, useIntl,
} from 'react-intl';
import DateTimePicker from '@react-native-community/datetimepicker';
import memoizeOne from 'memoize-one';
import { parseISO, formatISO } from 'date-fns';
import FieldWrapper from './FieldWrapper';
import { useFormattedFieldErrors } from './utils';

const styles = memoizeOne((theme) => ({
    label: {
        fontSize: theme.fontsizes.categoryTitle,
        color: theme.colors.defaultTextColor,
        marginHorizontal: 10,
        fontWeight: 'bold',
    },
    inputText: {
        color: theme.colors.defaultTextColor,
        fontSize: theme.fontsizes.text,
    },
    error: {
        color: theme.colors.danger,
        fontSize: theme.fontsizes.textSmall,
    },
}));

type Props = {
    field: {
        value: any,
        name: string,
    },
    form: {
        errors: Object,
        touched: Object,
        isSubmitting: boolean,
        handleChange: (name: string) => (value: string | null) => void,
        handleBlur: (name: string) => () => void,
    },
    required: boolean,
    label: string,
    help_text: string,
    disabled?: boolean,
}

const getDate = memoizeOne((dateString: string): Date => parseISO(dateString));

const DateInput = (props: Props): React.Node => {
    const {
        disabled,
        field: {
            name, value,
        },
        form: {
            errors, touched, isSubmitting,
            handleChange, handleBlur,
        },
        label,
        help_text: help,
        required,
    } = props;

    let errs = get(errors, name);
    errs = useFormattedFieldErrors(errs);

    const intl = useIntl();

    const { theme } = React.useContext(ThemeContext);

    const [show, setShow] = React.useState(false);

    const input = React.useRef();

    const onChangeCallback = React.useCallback((e, _val: null | Date) => {
        let formatted = null;

        if (_val) {
            formatted = formatISO(_val, { representation: 'date' });
        }

        if (input.current) {
            input.current.blur();
        }

        setShow(false);

        handleChange(name)(formatted);
    }, [handleChange, name, setShow]);

    const handleFocus = React.useCallback(() => {
        setShow(true);
    }, [setShow]);

    const onBlurCallback = React.useCallback(() => {
        handleBlur(name);
    }, [name, handleBlur]);

    return (
        <FieldWrapper
            required={required}
            helpText={help}
        >
            <Text style={styles(theme).label}>{required ? `${label} *` : label}</Text>
            <Input
                errorMessage={touched[name] && errs ? errs : undefined}
                errorStyle={styles(theme).error}
                placeholder={label}
                placeholderTextColor={theme.colors.defaultTextColor}
                onBlur={onBlurCallback}
                value={value ? intl.formatDate(getDate(value)) : ''}
                disabled={disabled || isSubmitting}
                onFocus={handleFocus}
                showSoftInputOnFocus={false}
                selectTextOnFocus={false}
                ref={input}
                inputStyle={styles(theme).inputText}
            />
            {
                show && (
                    <DateTimePicker
                        mode="date"
                        value={value ? getDate(value) : new Date()}
                        display="spinner"
                        onChange={onChangeCallback}
                    />
                )
            }
        </FieldWrapper>
    );
};

const messages = defineMessages({
    required: {
        id: 'textinput-required',
        defaultMessage: 'This field is required.',
    },
});

DateInput.getValidationSchema = (field: { validation: Object }) => {
    if (field.validation.required) {
        return Yup.date().required(messages.required).nullable();
    }

    return Yup.date().nullable();
};

DateInput.defaultProps = {
    disabled: false,
};

export default DateInput;
